// Enhanced Shopify API Service
// Uses Netlify Functions for secure server-side operations

const API_BASE_URL = '/.netlify/functions';

// Helper function for API calls
async function fetchAPI(endpoint, options = {}) {
  try {
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'API request failed');
    }

    return data;
  } catch (error) {
    console.error(`API Error (${endpoint}):`, error);
    throw error;
  }
}

// Product API
export const productApi = {
  // Get single product by handle
  async getProduct(handle) {
    return fetchAPI(`shopify-product?handle=${handle}`);
  },

  // Get multiple products
  async getProducts(first = 10, query = '') {
    return fetchAPI(`shopify-product?first=${first}&query=${encodeURIComponent(query)}`);
  },

  // Get product recommendations (if you add this to the Netlify function)
  async getRecommendations(productId) {
    return fetchAPI(`shopify-product?recommendations=${productId}`);
  },
};

// Cart API
export const cartApi = {
  // Create a new cart
  async create(lines = [], options = {}) {
    return fetchAPI('shopify-cart', {
      method: 'POST',
      body: JSON.stringify({
        action: 'create',
        lines,
        country: options.country || 'NL',
        language: options.language || 'NL',
        buyerIdentity: options.buyerIdentity,
      }),
    });
  },

  // Add items to cart
  async addLines(cartId, lines) {
    return fetchAPI('shopify-cart', {
      method: 'POST',
      body: JSON.stringify({
        action: 'add',
        cartId,
        lines,
      }),
    });
  },

  // Update cart lines
  async updateLines(cartId, lines) {
    return fetchAPI('shopify-cart', {
      method: 'POST',
      body: JSON.stringify({
        action: 'update',
        cartId,
        lines,
      }),
    });
  },

  // Remove cart lines
  async removeLines(cartId, lineIds) {
    return fetchAPI('shopify-cart', {
      method: 'POST',
      body: JSON.stringify({
        action: 'remove',
        cartId,
        lineIds,
      }),
    });
  },

  // Apply discount codes
  async applyDiscount(cartId, discountCodes) {
    return fetchAPI('shopify-cart', {
      method: 'POST',
      body: JSON.stringify({
        action: 'discount',
        cartId,
        discountCodes,
      }),
    });
  },

  // Update buyer identity
  async updateBuyerIdentity(cartId, buyerIdentity) {
    return fetchAPI('shopify-cart', {
      method: 'POST',
      body: JSON.stringify({
        action: 'updateBuyer',
        cartId,
        buyerIdentity,
      }),
    });
  },

  // Get cart details
  async get(cartId) {
    return fetchAPI('shopify-cart', {
      method: 'POST',
      body: JSON.stringify({
        action: 'get',
        cartId,
      }),
    });
  },
};

// Utility functions
export const utils = {
  // Format price
  formatPrice(amount, currencyCode = 'EUR', locale = 'nl-NL') {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  },

  // Calculate discount percentage
  calculateDiscount(price, compareAtPrice) {
    if (!compareAtPrice || compareAtPrice <= price) return 0;
    return Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
  },

  // Get variant by selected options
  getVariantByOptions(product, selectedOptions) {
    if (!product?.variants?.edges) return null;
    
    return product.variants.edges.find(({ node: variant }) => {
      return variant.selectedOptions.every(option => {
        const selected = selectedOptions[option.name];
        return selected === option.value;
      });
    })?.node;
  },

  // Get available options based on selected ones
  getAvailableOptions(product, selectedOptions = {}) {
    if (!product?.options) return {};
    
    const availableOptions = {};
    
    product.options.forEach(option => {
      availableOptions[option.name] = option.values.filter(value => {
        const testOptions = { ...selectedOptions, [option.name]: value };
        const variant = this.getVariantByOptions(product, testOptions);
        return variant?.availableForSale;
      });
    });
    
    return availableOptions;
  },

  // Check if variant is in stock
  isInStock(variant) {
    return variant?.availableForSale && (variant.quantityAvailable === undefined || variant.quantityAvailable > 0);
  },

  // Get stock status message
  getStockStatus(variant, lang = 'nl') {
    const messages = {
      nl: {
        inStock: 'Op voorraad',
        lowStock: 'Beperkte voorraad',
        outOfStock: 'Uitverkocht',
        preOrder: 'Pre-order',
      },
      en: {
        inStock: 'In stock',
        lowStock: 'Limited stock',
        outOfStock: 'Out of stock',
        preOrder: 'Pre-order',
      },
    };

    const t = messages[lang] || messages.nl;

    if (!variant?.availableForSale) {
      return { status: 'outOfStock', message: t.outOfStock, available: false };
    }

    if (variant.quantityAvailable !== undefined) {
      if (variant.quantityAvailable === 0) {
        return { status: 'outOfStock', message: t.outOfStock, available: false };
      } else if (variant.quantityAvailable < 10) {
        return { status: 'lowStock', message: t.lowStock, available: true };
      }
    }

    return { status: 'inStock', message: t.inStock, available: true };
  },

  // Parse Shopify ID
  parseShopifyId(id) {
    // Shopify IDs are in format: gid://shopify/Product/123456789
    const matches = id?.match(/gid:\/\/shopify\/(\w+)\/(\d+)/);
    if (matches) {
      return {
        type: matches[1],
        id: matches[2],
      };
    }
    return null;
  },

  // Build Shopify GID
  buildShopifyGid(type, id) {
    return `gid://shopify/${type}/${id}`;
  },
};

// Local storage helpers for cart persistence
export const storage = {
  CART_KEY: 'shopify_cart_id',
  
  getCartId() {
    return localStorage.getItem(this.CART_KEY);
  },
  
  setCartId(cartId) {
    localStorage.setItem(this.CART_KEY, cartId);
  },
  
  clearCartId() {
    localStorage.removeItem(this.CART_KEY);
  },
};

export default {
  product: productApi,
  cart: cartApi,
  utils,
  storage,
};